import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Receipt, Mail, AlertCircle } from 'lucide-react';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const bodyFont = "'Manrope', sans-serif";

function formatEur(value) {
  const n = Number(value || 0);
  return `€${n.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMonth(monthKey) {
  if (!monthKey) return '';
  const [y, m] = monthKey.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

export default function MyInvoicesPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/my`, { headers });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  const download = async (invoice) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `allure-${invoice.kind}-${invoice.number || invoice.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  return (
    <div
      data-testid="my-invoices-page"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--app-bg, #FFFFFF)',
        padding: '20px 20px 80px',
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <button
        type="button"
        onClick={goBack}
        data-testid="my-invoices-back"
        aria-label="Back"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: 'transparent',
          fontFamily: font,
          fontSize: 15,
          color: 'var(--brand, #703FF6)',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 14,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1 style={{ fontFamily: font, fontSize: 30, fontWeight: 700, color: 'var(--text, #111)', margin: 0 }}>
        Invoices &amp; Statements
      </h1>
      <p style={{ fontFamily: bodyFont, fontSize: 14, color: 'var(--text-muted, #666)', marginTop: 8 }}>
        Monthly VAT invoices (for venues) and earnings statements (for hosts). Each PDF is
        immutable and regeneratable at any time.
      </p>

      {loading && (
        <p style={{ marginTop: 24, color: 'var(--text-muted, #666)', fontFamily: bodyFont }}>
          Loading…
        </p>
      )}

      {!loading && items.length === 0 && (
        <div
          data-testid="my-invoices-empty"
          style={{
            marginTop: 32,
            padding: 24,
            borderRadius: 14,
            border: '1px dashed var(--border, #E5E7EB)',
            textAlign: 'center',
            color: 'var(--text-muted, #666)',
            fontFamily: bodyFont,
          }}
        >
          <FileText size={30} style={{ opacity: 0.4, marginBottom: 8 }} />
          <p style={{ margin: 0 }}>No invoices yet — your first monthly document will appear here.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul
          data-testid="my-invoices-list"
          style={{ listStyle: 'none', padding: 0, margin: '24px 0 0', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {items.map((inv) => (
            <li
              key={inv.id}
              data-testid={`my-invoice-row-${inv.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 16,
                padding: 16,
                borderRadius: 14,
                border: '1px solid var(--border, #E5E7EB)',
                background: 'var(--surface, #FAFAFA)',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {inv.kind === 'venue' ? (
                    <Receipt size={16} color="var(--brand, #703FF6)" />
                  ) : (
                    <FileText size={16} color="var(--brand, #703FF6)" />
                  )}
                  <strong style={{ fontFamily: font, fontSize: 16, color: 'var(--text, #111)' }}>
                    {inv.number}
                  </strong>
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: bodyFont,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: inv.kind === 'venue' ? 'rgba(112,63,246,0.12)' : 'rgba(46,164,79,0.12)',
                      color: inv.kind === 'venue' ? '#703FF6' : '#2EA44F',
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {inv.kind === 'venue' ? 'VAT Invoice' : 'Earnings Statement'}
                  </span>
                </div>
                <p style={{ margin: '6px 0 0', fontFamily: bodyFont, fontSize: 13, color: 'var(--text-muted, #666)' }}>
                  {formatMonth(inv.month_key)} · Issued {inv.issue_date} ·{' '}
                  {inv.kind === 'venue'
                    ? `Total ${formatEur(inv.total_gross)} (VAT incl.)`
                    : `Credited ${formatEur(inv.total_credited)}`}
                </p>
                {inv.email_status === 'failed' && (
                  <p style={{ margin: '6px 0 0', fontFamily: bodyFont, fontSize: 12, color: '#E11D48', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <AlertCircle size={12} /> Email delivery failed — download PDF manually.
                  </p>
                )}
                {inv.email_status === 'sent' && (
                  <p style={{ margin: '6px 0 0', fontFamily: bodyFont, fontSize: 12, color: '#2EA44F', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Mail size={12} /> Emailed to your inbox.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => download(inv)}
                data-testid={`my-invoice-download-${inv.id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid var(--brand, #703FF6)',
                  background: 'transparent',
                  color: 'var(--brand, #703FF6)',
                  padding: '8px 14px',
                  borderRadius: 10,
                  fontFamily: bodyFont,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Download size={14} /> PDF
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
